import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/common/Header';
import api from '../services/api';
import { Activity, CheckCircle, XCircle, TrendingUp, TrendingDown, Clock, Info } from 'lucide-react';

interface Execution {
  id: number;
  rule_id: number;
  rule_name: string;
  campaign_id: number;
  campaign_name: string;
  location_id: number;
  location_name: string;
  weather_metric: string;
  weather_value: number;
  threshold_value: number;
  bid_adjustment_type: string;
  bid_adjustment_percent: number;
  adjustment_target: 'bid' | 'budget';
  success: boolean;
  error_message: string | null;
  executed_at: string;
  result_json: any;
  delta_before_after: any;
  google_mutation_id: string | null;
  execution_time_ms: number;
}

export default function History() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dryRunMode, setDryRunMode] = useState(true); // Default to true for safety

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    loadExecutions();
    loadSettings();
  }, [isAuthenticated, navigate]);

  const loadExecutions = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/executions');
      setExecutions(response.data.executions);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load execution history');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      setDryRunMode(response.data.dryRunMode);
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Africa/Johannesburg', // Force SAST (UTC+2)
    });
  };

  const getActionIcon = (action_type: string) => {
    if (action_type.toLowerCase().includes('increase')) {
      return <TrendingUp className="w-5 h-5 text-green-400" />;
    }
    return <TrendingDown className="w-5 h-5 text-cyan-400" />;
  };

  const getStatusBadge = (success: boolean) => {
    if (success) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900 text-green-300">
          <CheckCircle className="w-3 h-3 mr-1" />
          Success
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900 text-red-300">
        <XCircle className="w-3 h-3 mr-1" />
        Failed
      </span>
    );
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">
            Execution History
          </h2>
          <p className="text-gray-400">
            View the last 50 bid adjustment executions and their results
          </p>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 bg-red-900 border border-red-700 rounded-lg p-4">
            <p className="text-red-200">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-12">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
              <span className="ml-3 text-gray-300">Loading execution history...</span>
            </div>
          </div>
        ) : executions.length === 0 ? (
          /* Empty State */
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-12 text-center">
            <Activity className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              No Execution History
            </h3>
            <p className="text-gray-400 mb-6">
              No bid adjustments have been executed yet. Create weather rules and wait for the automation to run.
            </p>
            <button
              onClick={() => navigate('/rules')}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              Create Weather Rules
            </button>
          </div>
        ) : (
          /* Executions List */
          <div className="space-y-4">
            {executions.map((execution) => (
              <div
                key={execution.id}
                className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-6 hover:border-cyan-600 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      {getActionIcon(execution.bid_adjustment_type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Rule & Campaign */}
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-semibold text-white truncate">
                          {execution.rule_name || `Rule #${execution.rule_id}`}
                        </h3>
                        {getStatusBadge(execution.success)}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                        {/* Campaign */}
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Campaign</p>
                          <p className="text-sm text-gray-300">{execution.campaign_name}</p>
                        </div>

                        {/* Location */}
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Location</p>
                          <p className="text-sm text-gray-300">{execution.location_name}</p>
                        </div>

                        {/* Weather Condition */}
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Weather Condition</p>
                          <p className="text-sm text-gray-300">
                            {execution.weather_metric}: {execution.weather_value}
                            {execution.weather_metric.toLowerCase().includes('temp') ? '°C' : ''}
                            {execution.weather_metric.toLowerCase().includes('rain') ? '%' : ''}
                            {' '}
                            (threshold: {execution.threshold_value})
                          </p>
                        </div>

                        {/* Adjustment */}
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                            {(execution.adjustment_target || 'bid') === 'budget' ? 'Budget' : 'Bid'} Adjustment
                            <div className="relative group">
                              <Info className="w-3 h-3 text-gray-500 hover:text-cyan-400 cursor-help" />
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 border border-cyan-700 rounded-lg text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 w-64">
                                <p className="whitespace-normal">
                                  <span className="text-green-400 font-semibold">Green (+)</span>: Bid/budget increase — more aggressive bidding, higher spend to capitalize on favorable conditions<br/><br/>
                                  <span className="text-cyan-400 font-semibold">Blue (−)</span>: Bid/budget decrease — cost savings and budget control when conditions aren't optimal
                                </p>
                              </div>
                            </div>
                          </p>
                          <p className={`text-sm font-semibold ${
                            execution.bid_adjustment_type.toLowerCase().includes('increase')
                              ? 'text-green-400'
                              : 'text-cyan-400'
                          }`}>
                            {execution.bid_adjustment_type.toLowerCase().includes('increase') ? '+' : '−'}
                            {(execution.adjustment_target || 'bid') === 'budget'
                              ? `R${Number(execution.bid_adjustment_percent || 0).toFixed(2)}`
                              : `${execution.bid_adjustment_percent || 0}%`
                            }
                          </p>
                        </div>

                        {/* Execution Time */}
                        {execution.execution_time_ms && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                              Execution Time
                              <div className="relative group">
                                <Info className="w-3 h-3 text-gray-500 hover:text-cyan-400 cursor-help" />
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 border border-cyan-700 rounded-lg text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 w-56">
                                  <p className="whitespace-normal">How long it took the Google Ads API to respond (lower is better)</p>
                                </div>
                              </div>
                            </p>
                            <p className="text-sm text-gray-300 flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {execution.execution_time_ms}ms
                            </p>
                          </div>
                        )}

                        {/* Google Mutation ID */}
                        {execution.google_mutation_id && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                              Google Mutation ID
                              <div className="relative group">
                                <Info className="w-3 h-3 text-gray-500 hover:text-cyan-400 cursor-help" />
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 border border-cyan-700 rounded-lg text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 w-64">
                                  <p className="whitespace-normal">Unique ID from Google Ads API for this change (useful for troubleshooting with Google support)</p>
                                </div>
                              </div>
                            </p>
                            <p className="text-xs text-gray-400 font-mono truncate">
                              {execution.google_mutation_id}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Error Message */}
                      {execution.error_message && (
                        <div className="mt-4 p-3 bg-red-900 bg-opacity-30 border border-red-700 rounded">
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Error</p>
                          <p className="text-sm text-red-300">{execution.error_message}</p>
                        </div>
                      )}

                      {/* Delta (Before/After) */}
                      {execution.delta_before_after && (
                        <div className="mt-4 p-3 bg-gray-900 bg-opacity-50 border border-gray-600 rounded">
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Budget Change</p>
                          <div className="flex items-center space-x-4 text-sm">
                            <div>
                              <span className="text-gray-500">Before:</span>
                              <span className="ml-2 text-gray-300 font-mono">
                                {JSON.stringify(execution.delta_before_after.before || 'N/A')}
                              </span>
                            </div>
                            <span className="text-gray-600">→</span>
                            <div>
                              <span className="text-gray-500">After:</span>
                              <span className="ml-2 text-cyan-400 font-mono">
                                {JSON.stringify(execution.delta_before_after.after || 'N/A')}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Timestamp */}
                  <div className="flex-shrink-0 ml-4 text-right">
                    <p className="text-xs text-gray-500">Executed</p>
                    <p className="text-sm text-gray-300 whitespace-nowrap">
                      {formatDate(execution.executed_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Footer */}
        {!loading && executions.length > 0 && (
          <div className="mt-8 bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <p className="text-2xl font-bold text-cyan-400">{executions.length}</p>
                <p className="text-sm text-gray-400 mt-1">Total Executions</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-400">
                  {executions.filter(e => e.success).length}
                </p>
                <p className="text-sm text-gray-400 mt-1">Successful</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-400">
                  {executions.filter(e => !e.success).length}
                </p>
                <p className="text-sm text-gray-400 mt-1">Failed</p>
              </div>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className={`mt-6 ${dryRunMode ? 'bg-cyan-900 bg-opacity-20 border-cyan-700' : 'bg-amber-900 bg-opacity-20 border-amber-700'} border rounded-lg p-4`}>
          <div className="flex">
            <div className="flex-shrink-0">
              <Activity className={`h-5 w-5 ${dryRunMode ? 'text-cyan-400' : 'text-amber-400'}`} />
            </div>
            <div className="ml-3">
              <p className={`text-sm ${dryRunMode ? 'text-cyan-300' : 'text-amber-300'}`}>
                <strong>💡 Tip:</strong> Execution history shows all bid adjustments made by weather rules.
                The system runs every 30 minutes and evaluates all active rules.
                {dryRunMode ? (
                  <>
                    Currently in <strong>dry run mode</strong> - no real changes are made until you disable dry run.
                  </>
                ) : (
                  <>
                    Currently in <strong className="text-amber-200">LIVE MODE</strong> - all adjustments are applied to Google Ads in real-time.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
